import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Loader2, ShieldAlert } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { COMPANY } from "@/lib/site";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !password) {
      setError("Please enter both your email and password.");
      return;
    }
    setLoading(true);
    try {
      await login(trimmedEmail, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-[calc(100vh-5rem)] bg-[#2E1605] flex items-center justify-center px-6 py-20">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <img src={COMPANY.logo} alt={COMPANY.name} className="h-16 w-16 object-contain bg-white/95 rounded-sm p-1" />
          <h1 className="font-heading font-black text-2xl text-white mt-6 flex items-center gap-2">
            <Lock className="h-5 w-5 text-[#00B4C4]" /> Admin Access
          </h1>
          <p className="text-[#B8ABA2] text-sm mt-2">Restricted area — authorized personnel only.</p>
        </div>

        <form data-testid="admin-login-form" onSubmit={onSubmit} noValidate className="bg-white rounded-sm p-8 space-y-5">
          {error && (
            <div data-testid="admin-login-error" className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-sm px-4 py-3">
              <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" /> {error}
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="admin-email">Email</Label>
            <Input id="admin-email" type="text" inputMode="email" data-testid="admin-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@betatechsystems.com" className="focus-visible:ring-[#00B4C4]" autoComplete="username" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="admin-password">Password</Label>
            <Input id="admin-password" type="password" data-testid="admin-password-input" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••••••" className="focus-visible:ring-[#00B4C4]" autoComplete="current-password" />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#1A110B] text-white font-heading font-black tracking-wider uppercase text-sm px-8 py-4 rounded-sm hover:bg-black transition-colors disabled:opacity-60"
          >
            {loading ? (<><Loader2 className="h-4 w-4 animate-spin" /> Signing in...</>) : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}
